/**
 * SectionDiffStrip — compact live diff between a draft object and its
 * currently-published snapshot. Used inside SectionsManager so admins see
 * exactly what will ship when they hit Publish.
 *
 * Props:
 *   draft      — currently edited content (in-memory)
 *   published  — the last-published snapshot
 */
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Minus, RefreshCw, CheckCircle2 } from 'lucide-react';

// ── Deep, order-insensitive value equality for JSON-y objects ──
const isEqual = (a, b) => {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== typeof b) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => isEqual(v, b[i]));
  }
  if (typeof a === 'object') {
    const ak = Object.keys(a); const bk = Object.keys(b);
    if (ak.length !== bk.length) return false;
    return ak.every((k) => isEqual(a[k], b[k]));
  }
  return false;
};

// ── Walk both trees and emit a flat change list ──
// Kinds: 'added' (draft has, published lacks), 'removed', 'changed' (leaf value diff).
// For arrays we compare by index; length changes surface as added/removed rows.
const diffTrees = (draft, published, basePath = '') => {
  const out = [];
  const dIsObj = draft && typeof draft === 'object' && !Array.isArray(draft);
  const pIsObj = published && typeof published === 'object' && !Array.isArray(published);
  const dIsArr = Array.isArray(draft);
  const pIsArr = Array.isArray(published);

  // Leaf-vs-leaf, or type mismatch → single 'changed'
  if (!dIsObj && !pIsObj && !dIsArr && !pIsArr) {
    if (!isEqual(draft, published)) {
      out.push({ path: basePath || '(root)', kind: 'changed', before: published, after: draft });
    }
    return out;
  }
  if ((dIsObj !== pIsObj) || (dIsArr !== pIsArr)) {
    out.push({ path: basePath || '(root)', kind: 'changed', before: published, after: draft });
    return out;
  }

  // Both arrays
  if (dIsArr && pIsArr) {
    const max = Math.max(draft.length, published.length);
    for (let i = 0; i < max; i++) {
      const dv = draft[i]; const pv = published[i];
      const p = `${basePath}[${i}]`;
      if (i >= published.length) {
        out.push({ path: p, kind: 'added', after: dv });
      } else if (i >= draft.length) {
        out.push({ path: p, kind: 'removed', before: pv });
      } else {
        out.push(...diffTrees(dv, pv, p));
      }
    }
    return out;
  }

  // Both plain objects
  const keys = new Set([...Object.keys(draft || {}), ...Object.keys(published || {})]);
  for (const k of keys) {
    const p = basePath ? `${basePath}.${k}` : k;
    const dv = draft?.[k]; const pv = published?.[k];
    if (dv === undefined && pv !== undefined) out.push({ path: p, kind: 'removed', before: pv });
    else if (dv !== undefined && pv === undefined) out.push({ path: p, kind: 'added', after: dv });
    else out.push(...diffTrees(dv, pv, p));
  }
  return out;
};

const formatValue = (v) => {
  if (v === undefined || v === null || v === '') return <span className="italic text-slate-400">empty</span>;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v.length > 80 ? v.slice(0, 80) + '…' : v;
  // object / array — render a short JSON preview
  const j = JSON.stringify(v);
  return j.length > 80 ? j.slice(0, 80) + '…' : j;
};

const KIND_STYLES = {
  added:   { pill: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Plus,   label: 'added' },
  removed: { pill: 'bg-red-100 text-red-700 border-red-200',            icon: Minus,  label: 'removed' },
  changed: { pill: 'bg-amber-100 text-amber-700 border-amber-200',      icon: RefreshCw, label: 'changed' },
};

const SectionDiffStrip = ({ draft, published }) => {
  const [open, setOpen] = useState(false);

  const changes = useMemo(() => {
    // A brand-new section (never published) has published_content = {}.
    // Treat all draft keys as "added" then.
    return diffTrees(draft || {}, published || {});
  }, [draft, published]);

  // No changes → subtle green banner
  if (changes.length === 0) {
    return (
      <div
        className="flex items-center gap-2 text-xs px-3 py-2 rounded-md border bg-emerald-50 border-emerald-200 text-emerald-700"
        data-testid="diff-strip-clean"
      >
        <CheckCircle2 className="w-3.5 h-3.5" />
        <span>Draft matches published — nothing to ship.</span>
      </div>
    );
  }

  const counts = { added: 0, removed: 0, changed: 0 };
  changes.forEach((c) => { counts[c.kind] += 1; });

  return (
    <div
      className="rounded-md border bg-amber-50/70 border-amber-200 overflow-hidden"
      data-testid="diff-strip"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
        data-testid="diff-strip-toggle"
      >
        {open ? <ChevronDown className="w-4 h-4 text-amber-700" /> : <ChevronRight className="w-4 h-4 text-amber-700" />}
        <span className="text-xs font-semibold text-amber-800">
          {changes.length} change{changes.length === 1 ? '' : 's'} ready to publish
        </span>
        <div className="ml-2 flex items-center gap-1.5">
          {counts.added > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="diff-count-added">
              <Plus className="w-2.5 h-2.5" />{counts.added}
            </span>
          )}
          {counts.removed > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border bg-red-100 text-red-700 border-red-200" data-testid="diff-count-removed">
              <Minus className="w-2.5 h-2.5" />{counts.removed}
            </span>
          )}
          {counts.changed > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border bg-amber-100 text-amber-800 border-amber-300" data-testid="diff-count-changed">
              <RefreshCw className="w-2.5 h-2.5" />{counts.changed}
            </span>
          )}
        </div>
        <span className="ml-auto text-[10px] text-amber-700/80">
          {open ? 'hide details' : 'show details'}
        </span>
      </button>
      {open && (
        <div className="border-t border-amber-200 bg-white max-h-56 overflow-y-auto" data-testid="diff-strip-body">
          <ul className="divide-y">
            {changes.slice(0, 100).map((c, i) => {
              const style = KIND_STYLES[c.kind];
              const Icon = style.icon;
              return (
                <li key={`${c.path}-${i}`} className="px-3 py-1.5 text-xs" data-testid={`diff-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold ${style.pill}`}>
                      <Icon className="w-2.5 h-2.5" />
                      {style.label}
                    </span>
                    <code className="font-mono text-[11px] text-slate-700 break-all">{c.path}</code>
                  </div>
                  {c.kind !== 'added' && (
                    <div className="mt-0.5 ml-1 text-[11px] text-slate-500">
                      <span className="text-red-600 line-through mr-1">{formatValue(c.before)}</span>
                    </div>
                  )}
                  {c.kind !== 'removed' && (
                    <div className="ml-1 text-[11px]">
                      <span className="text-emerald-700">{formatValue(c.after)}</span>
                    </div>
                  )}
                </li>
              );
            })}
            {changes.length > 100 && (
              <li className="px-3 py-1.5 text-[11px] italic text-slate-500">
                Showing first 100 of {changes.length} changes.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SectionDiffStrip;
